import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../dashboard/Dashboard.css';
import '../business/Business.css';
import './OrderDetailPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';
import EditStockMovementModal from './components/EditStockMovementModal';
import OrderInfoSection from './components/OrderInfoSection';
import OrderProductsSection, { isShipmentMovement } from './components/OrderProductsSection';
import OrderActionsSection from './components/OrderActionsSection';
import OrderFilterSelectModal from './components/OrderFilterSelectModal';
import CancelOrderConfirmModal from './components/CancelOrderConfirmModal';
import PaymentStatusSelectModal, { qrImageUrl } from './components/PaymentStatusSelectModal';
import { PaymentProviderTypes } from '../../types/PaymentProviderTypes';
import { ProviderStatusTypes } from '../../types/ProviderStatusTypes';
import { PaymentStatusTypes } from '../../types/PaymentStatusTypes';
import {
  OrderStatusTypes,
  buildStatusFilterOptions,
  sortCustomOrderStatuses,
} from '../../types/OrderStatusTypes';
import { getOrderTypeId, isExpenseOrder, isInvestmentOrder, isReplenishOrder } from '../../types/OrderTypes';

function OrderDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId, orderId } = useParams();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [order, setOrder] = React.useState(null);
  const [stockMovements, setStockMovements] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [paymentError, setPaymentError] = React.useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = React.useState(false);
  const [editingMovement, setEditingMovement] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState(null);
  const [customStatuses, setCustomStatuses] = React.useState([]);
  const [statusesReady, setStatusesReady] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const [receiptSubmitting, setReceiptSubmitting] = React.useState(false);
  const [receiptError, setReceiptError] = React.useState(null);
  const [statusError, setStatusError] = React.useState(null);
  const [paymentProviders, setPaymentProviders] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    setStatusesReady(false);
    (async () => {
      try {
        const res = await requestWithMeta('/operator/custom-order-statuses', 'GET');
        if (cancelled) return;
        const list = Array.isArray(res?.data?.customOrderStatuses)
          ? res.data.customOrderStatuses
          : [];
        setCustomStatuses(sortCustomOrderStatuses(list));
      } catch (_) {
        if (!cancelled) setCustomStatuses([]);
      } finally {
        if (!cancelled) setStatusesReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [requestWithMeta]);

  React.useEffect(() => {
    if (!branchId || branchId === 'store') {
      setPaymentProviders([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/branches/${encodeURIComponent(branchId)}/payment-providers`,
          'GET'
        );
        if (cancelled) return;
        const list = Array.isArray(res?.data?.paymentProviders) ? res.data.paymentProviders : [];
        setPaymentProviders(
          list.filter((row) =>
            Number(row.providerType) === PaymentProviderTypes.O_TRADE
            && Number(row.statusId) !== ProviderStatusTypes.INACTIVE
          )
        );
      } catch (_) {
        if (!cancelled) setPaymentProviders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId, requestWithMeta]);

  React.useEffect(() => {
    if (!orderId) {
      navigate('/branch/store', { replace: true });
      return;
    }
    if (!statusesReady) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/orders/${encodeURIComponent(orderId)}`,
          'GET'
        );

        if (cancelled) return;

        if (!res?.ok) {
          setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить заказ');
          setOrder(null);
          setStockMovements([]);
          setPayments([]);
          return;
        }

        setOrder(res?.data?.order ?? null);
        setStockMovements(Array.isArray(res?.data?.stockMovements) ? res.data.stockMovements : []);
        setPayments(Array.isArray(res?.data?.payments) ? res.data.payments : []);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setOrder(null);
          setStockMovements([]);
          setPayments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [orderId, requestWithMeta, navigate, statusesReady]);

  const handleBack = () => {
    const backTo = location.state?.backTo;
    if (backTo?.pathname) {
      navigate({
        pathname: backTo.pathname,
        search: backTo.search || '',
      });
      return;
    }
    if (branchId) {
      navigate({
        pathname: `/branch/${branchId}/orders`,
        search: location.search,
      });
    } else {
      navigate('/branch/store');
    }
  };

  const handleIssueReceipt = async (originFdNumber) => {
    if (!orderId) return;
    setReceiptError(null);
    setReceiptSubmitting(true);
    try {
      const body = originFdNumber == null ? null : { originFdNumber };
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}/receipt`,
        'POST',
        body
      );
      if (!res?.ok) {
        setReceiptError(res?.data?.error || res?.data?.message || 'Не удалось выбить чек');
        return;
      }
      const reload = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}`,
        'GET'
      );
      if (!reload?.ok) {
        setReceiptError(reload?.data?.error || reload?.data?.message || 'Чек отправлен, но заказ не обновился');
        return;
      }
      setOrder(reload?.data?.order ?? null);
      setStockMovements(Array.isArray(reload?.data?.stockMovements) ? reload.data.stockMovements : []);
      setPayments(Array.isArray(reload?.data?.payments) ? reload.data.payments : []);
    } catch (_) {
      setReceiptError('Ошибка запроса');
    } finally {
      setReceiptSubmitting(false);
    }
  };

  const handleStockMovementSaved = (updated) => {
    if (!updated) return;
    const id = updated?.id ?? updated?._id;
    setStockMovements((prev) =>
      prev.map((m) => (m?.id ?? m?._id) === id ? { ...m, ...updated } : m)
    );
    setEditingMovement(null);
  };

  const handleStatusSelect = (nextStatusId) => {
    if (nextStatusId == null) return;
    const currentStatusId = order?.statusId ?? order?.status_id;
    if (
      Number(nextStatusId) === OrderStatusTypes.CANCELED &&
      Number(currentStatusId) !== OrderStatusTypes.CANCELED
    ) {
      setCancelConfirmOpen(true);
      return;
    }
    handleStatusChange(nextStatusId);
  };

  const handleStatusChange = async (newStatusId) => {
    const previousStatusId = order?.statusId ?? order?.status_id;
    setStatusError(null);
    try {
      setOrder((prev) => ({ ...prev, statusId: null, status_id: null }));
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}/status`,
        'PUT',
        { statusId: newStatusId }
      );
      if (res?.ok) {
        setOrder((prev) => ({ ...prev, statusId: newStatusId, status_id: newStatusId }));
        return;
      }
      setOrder((prev) => ({ ...prev, statusId: previousStatusId, status_id: previousStatusId }));
      setStatusError(res?.data?.error || res?.data?.message || 'Не удалось изменить статус');
    } catch (_) {
      setOrder((prev) => ({ ...prev, statusId: previousStatusId, status_id: previousStatusId }));
      setStatusError('Не удалось изменить статус');
    }
  };

  const handlePaymentAction = async (body) => {
    const previousPaymentStatusId = order?.paymentStatusId ?? order?.payment_status_id;
    const previousDebt = order?.debt;
    const previousPayments = payments;
    setPaymentError(null);
    setPaymentSubmitting(true);
    try {
      setOrder((prev) => ({ ...prev, paymentStatusId: null, payment_status_id: null }));
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}/payment-status`,
        'PUT',
        body
      );
      if (res?.ok) {
        const nextPaymentStatusId = res?.data?.paymentStatusId ?? previousPaymentStatusId;
        const nextPayments = Array.isArray(res?.data?.payments) ? res.data.payments : previousPayments;
        setPayments(nextPayments);
        setOrder((prev) => ({
          ...prev,
          paymentStatusId: nextPaymentStatusId,
          payment_status_id: nextPaymentStatusId,
          debt: res?.data?.debt ?? prev?.debt,
        }));
        return true;
      }
      setPaymentError(res?.data?.error || res?.data?.message || 'Не удалось изменить оплату');
      setPayments(previousPayments);
      setOrder((prev) => ({
        ...prev,
        paymentStatusId: previousPaymentStatusId,
        payment_status_id: previousPaymentStatusId,
        debt: previousDebt,
      }));
      return false;
    } catch (_) {
      setPaymentError('Ошибка изменения оплаты');
      setPayments(previousPayments);
      setOrder((prev) => ({
        ...prev,
        paymentStatusId: previousPaymentStatusId,
        payment_status_id: previousPaymentStatusId,
        debt: previousDebt,
      }));
      return false;
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const mergeGatewayPayment = React.useCallback((data, previousPayment) => {
    const payment = data?.payment;
    if (!payment) return null;
    setPayments((prev) => {
      const id = payment.id ?? payment._id;
      const existing = prev.find((row) => (row?.id ?? row?._id) === id);
      const withQr = {
        ...(existing || {}),
        ...(previousPayment || {}),
        ...payment,
        qrUrl: payment.qrUrl
          || qrImageUrl(data?.invoice)
          || existing?.qrUrl
          || previousPayment?.qrUrl
          || null,
      };
      const rest = prev.filter((row) => (row?.id ?? row?._id) !== id);
      return [withQr, ...rest];
    });
    const withQr = {
      ...(previousPayment || {}),
      ...payment,
      qrUrl: payment.qrUrl || qrImageUrl(data?.invoice) || previousPayment?.qrUrl || null,
    };
    if (data.paymentStatusId != null || data.debt != null) {
      setOrder((prev) => ({
        ...prev,
        paymentStatusId: data.paymentStatusId ?? prev?.paymentStatusId,
        payment_status_id: data.paymentStatusId ?? prev?.payment_status_id,
        debt: data.debt ?? prev?.debt,
      }));
    }
    return withQr;
  }, []);

  const handleInitOtrade = async ({ providerId, amount }) => {
    setPaymentError(null);
    setPaymentSubmitting(true);
    try {
      const res = await requestWithMeta(
        `/operator/branches/${encodeURIComponent(branchId)}/payments/init`,
        'POST',
        { providerId, orderId: Number(orderId), amount }
      );
      if (!res?.ok) {
        return { error: res?.data?.error || res?.data?.message || 'Не удалось создать оплату' };
      }
      const payment = mergeGatewayPayment(res.data);
      return payment ? { payment } : { error: 'Не удалось создать оплату' };
    } catch (_) {
      return { error: 'Ошибка создания оплаты' };
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handlePollPayment = React.useCallback(async (paymentId) => {
    try {
      const res = await requestWithMeta(
        `/operator/payments/${encodeURIComponent(paymentId)}/status`,
        'GET'
      );
      if (!res?.ok || !res?.data?.payment) return null;
      return mergeGatewayPayment(res.data);
    } catch (_) {
      return null;
    }
  }, [requestWithMeta, mergeGatewayPayment]);

  const handleCancelGateway = async (paymentId) => {
    setPaymentError(null);
    setPaymentSubmitting(true);
    try {
      const res = await requestWithMeta(
        `/operator/payments/${encodeURIComponent(paymentId)}/cancel`,
        'POST'
      );
      if (!res?.ok) {
        setPaymentError(res?.data?.error || res?.data?.message || 'Не удалось отменить оплату');
        return false;
      }
      mergeGatewayPayment(res.data);
      return true;
    } catch (_) {
      setPaymentError('Ошибка отмены оплаты');
      return false;
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleAskDelete = () => {
    setDeleteError(null);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteError(null);
    try {
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}`,
        'DELETE'
      );
      if (res?.ok) {
        setDeleteConfirmOpen(false);
        handleBack();
        return;
      }
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить запись');
    } catch (_) {
      setDeleteError('Ошибка удаления');
    }
  };

  if (loading) {
    return (
      <div className="order-detail-root">
        <CreateBusinessHeader title="Заказ" subtitle="" onBack={handleBack} />
        <main className="order-detail-content">
          <div className="order-detail-skeleton order-detail-skeleton--block" />
          <div className="order-detail-skeleton order-detail-skeleton--block" />
          <div className="order-detail-skeleton order-detail-skeleton--block" />
        </main>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-root">
        <CreateBusinessHeader title="Заказ" subtitle="" onBack={handleBack} />
        <main className="order-detail-content">
          <div className="order-detail-error">{error || 'Заказ не найден'}</div>
        </main>
      </div>
    );
  }

  const id = order?.id ?? order?._id;
  const statusId = order?.statusId ?? order?.status_id;
  const typeId = getOrderTypeId(order);
  const paymentStatusId = order?.paymentStatusId ?? order?.payment_status_id;
  const orderTotal = stockMovements.reduce((sum, m) => {
    const qty = m?.quantity ?? 0;
    const p = m?.productPrice ?? m?.product_price ?? 0;
    const price = typeof p === 'number' ? p : parseFloat(p) || 0;
    return sum + qty * price;
  }, 0);
  const currentDebtRaw = order?.debt;
  const currentDebt = typeof currentDebtRaw === 'number' ? currentDebtRaw : parseFloat(currentDebtRaw) || 0;
  const isReplenishment = isReplenishOrder(order);
  const isExpense = isExpenseOrder(order);
  const isInvestment = isInvestmentOrder(order);
  const isCanceledUnpaid =
    !isReplenishment &&
    !isExpense &&
    !isInvestment &&
    Number(statusId) === OrderStatusTypes.CANCELED &&
    Number(paymentStatusId ?? PaymentStatusTypes.NOT_PAID) === PaymentStatusTypes.NOT_PAID;
  const statusLabel = isReplenishment
    ? `Пополнение № ${id}`
    : isExpense
      ? `Расход № ${id}`
      : isInvestment
        ? `Инвестиция № ${id}`
        : `Заказ № ${id}`;

  return (
    <div className="order-detail-root">
      <CreateBusinessHeader
        title={statusLabel}
        subtitle=""
        onBack={handleBack}
      />

      <main className="order-detail-content">
        <OrderInfoSection
          order={order}
          isSystemOrder={isReplenishment || isExpense || isInvestment}
          onPaymentStatusOpen={() => setPaymentModalOpen(true)}
          onIssueReceipt={handleIssueReceipt}
          receiptSubmitting={receiptSubmitting}
          receiptError={receiptError}
          statusError={statusError}
          customStatuses={customStatuses}
        />

        <OrderProductsSection
          stockMovements={stockMovements}
          isReplenishment={isReplenishment}
          hideQuantity={isExpense || isInvestment}
          onEditMovement={setEditingMovement}
        />
      </main>

      <OrderActionsSection
        typeId={typeId}
        showDelete={isCanceledUnpaid}
        onStatusOpen={() => setStatusModalOpen(true)}
        onDelete={handleAskDelete}
        statusDisabled={statusId == null}
      />

      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статус"
        options={buildStatusFilterOptions(customStatuses)}
        selectedIds={statusId == null ? [] : [Number(statusId)]}
        colorizeStatus
        onApply={(ids) => handleStatusSelect(ids[0])}
        onClose={() => setStatusModalOpen(false)}
      />

      <CancelOrderConfirmModal
        isOpen={cancelConfirmOpen}
        orderLabel={statusLabel}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          handleStatusChange(OrderStatusTypes.CANCELED);
        }}
      />

      <PaymentStatusSelectModal
        isOpen={paymentModalOpen}
        payments={payments}
        currentDebt={currentDebt}
        totalPrice={orderTotal}
        providers={paymentProviders}
        submitting={paymentSubmitting}
        error={paymentError}
        onApply={handlePaymentAction}
        onInitOtrade={handleInitOtrade}
        onPollStatus={handlePollPayment}
        onCancelGateway={handleCancelGateway}
        onClose={() => {
          if (paymentSubmitting) return;
          setPaymentModalOpen(false);
          setPaymentError(null);
        }}
      />

      {editingMovement ? (
        <EditStockMovementModal
          movement={editingMovement}
          showPrice={!isReplenishment}
          hideQuantity={isExpense || isInvestment || isShipmentMovement(editingMovement)}
          onClose={() => setEditingMovement(null)}
          onSaved={handleStockMovementSaved}
          requestWithMeta={requestWithMeta}
        />
      ) : null}

      <DeleteProductModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          if (httpLoading) return;
          setDeleteConfirmOpen(false);
          setDeleteError(null);
        }}
        title={
          isReplenishment
            ? 'Удалить пополнение'
            : isExpense
              ? 'Удалить расход'
              : isInvestment
                ? 'Удалить инвестицию'
                : 'Удалить заказ'
        }
        productName={statusLabel}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default OrderDetailPage;
